import { useState } from 'react';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Eye, EyeOff } from 'lucide-react';

interface ControlledPasswordProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  description?: string;
}

export function ControlledPassword<T extends FieldValues>({
  control,
  name,
  label,
  placeholder = 'Password',
  disabled = false,
  required = false,
  className,
}: ControlledPasswordProps<T>) {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const toggleVisibility = () => setIsVisible((prevState) => !prevState);

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <span className="text-destructive -ml-1">*</span>}
        </Label>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-1">
            <div className="relative">
              <Input
                {...field}
                id={name}
                type={isVisible ? 'text' : 'password'}
                placeholder={placeholder}
                disabled={disabled}
                aria-invalid={fieldState.error ? 'true' : 'false'}
                autoComplete="off"
                className={cn(
                  'pe-9',
                  fieldState.error &&
                  'border-destructive focus-visible:ring-destructive/20'
                )}
              />
              <button
                type="button"
                aria-controls={name}
                aria-label={isVisible ? 'Hide password' : 'Show password'}
                aria-pressed={isVisible}
                className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-md text-muted-foreground/80 outline-none transition-[color,box-shadow] hover:text-foreground focus:z-10 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                onClick={toggleVisibility}
                disabled={disabled}
              >
                {isVisible ? (
                  <EyeOff aria-hidden="true" size={16} />
                ) : (
                  <Eye aria-hidden="true" size={16} />
                )}
              </button>
            </div>
            {fieldState.error && (
              <motion.span
                animate={{
                  opacity: fieldState.error ? 1 : 0,
                  y: fieldState.error ? 0 : -10,
                }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="text-destructive block min-h-[20px] text-xs font-medium"
              >
                {fieldState.error?.message}
              </motion.span>
            )}
          </div>
        )}
      />
    </div>
  );
}
